import { Component, Input, ViewChild, ElementRef, OnInit, NgZone } from '@angular/core';
import { Subscription } from 'rxjs';
import { LoaderService } from './../../../app/services/loader.service';
import { ActivityViewHelperService } from './../../services/activity-view-helper.service';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { GroupHelperService } from 'modules/shared/services/groups-helper.service';
import { GetStreamCommentsService } from 'modules/shared/services/getstream-comments.service';
import { TranslateService } from '@ngx-translate/core';
import { TextEditor } from '../text-editor/text-editor.component';
import { appSetting }  from '../../../../config/appSettings';
import { AuthService } from '../../../app/services/auth.service';

declare var $: any;

@Component({
    selector: 'collaborative-skill',
    templateUrl: './collaborative-skill.template.html',
    styleUrls: ['./collaborative-skill.style.scss'],
    standalone: false
})
export class CollaborativeSkill implements OnInit {
    @ViewChild('contentContainer') private readonly contentContainer: ElementRef;
    @ViewChild('closecollabConfirmationModalBtn') closecollabConfirmationBtn: ElementRef;
    @ViewChild('showErrorModalBtn') showErrorModalBtn: ElementRef;
    @ViewChild(TextEditor) private readonly textEditor: TextEditor;
    completeCollabActivity: Subscription;
    changesNotSavedSubscription: Subscription;
    routeSubscription: Subscription;
    @Input('collabTask') collabTask :any;
    @Input('productMeta') productMeta : any;
    @Input('analytics') analytics: any;
    hiddenInst : boolean = false; 
    scrollControlVisible : boolean = false;
    scrollUp : boolean = false;
    public isLastActivity: boolean = false;
    groupDetails : any;
    groupId :string = '';
    isGroupArchived: boolean = false;
    itemId :string = '';
    groupMembers = [];
    isTeacher : boolean = false;
    showSubmitLoader : boolean = false;
    isClassExpired: boolean = false;
    private lastReadCommentTime: any;
    private lastReadCommentkey: string = '';
    private serverClientTimeOffsetMs: number | null = null;

    commentsData:any;

    translationTexts = {
      'JUST_NOW': 'SHARED.COLLAB.ELAPSED_TIME.JUST_NOW',
      'MINUTE_AGO': 'SHARED.COLLAB.ELAPSED_TIME.MINUTE_AGO',
      'MINUTES_AGO': 'SHARED.COLLAB.ELAPSED_TIME.MINUTES_AGO',
      'HOUR_AGO': 'SHARED.COLLAB.ELAPSED_TIME.HOUR_AGO',
      'HOURS_AGO': 'SHARED.COLLAB.ELAPSED_TIME.HOURS_AGO',
      'DAY_AGO': 'SHARED.COLLAB.ELAPSED_TIME.DAY_AGO',
      'DAYS_AGO': 'SHARED.COLLAB.ELAPSED_TIME.DAYS_AGO',
    };

    timeElapsedCounter: any;
    latestCommentId: string = '';
    pollingSetInterval: any;
    showCommentsLoader: boolean = false;
    errorType: string = '';
    contentHeightObserver: any;

    constructor (private ngZone: NgZone, 
      private activityViewHelperService: ActivityViewHelperService,
      private router: Router,
      private route: ActivatedRoute,
      private loaderService: LoaderService,
      private translationService: TranslateService,
      private commentsService: GetStreamCommentsService,
      private authService: AuthService,
      private groupHelperService: GroupHelperService){
        const translationKeysToFetch = [];
        Object.keys(this.translationTexts).forEach(key => {
            translationKeysToFetch.push(this.translationTexts[key]);
        });
        this.translationService.get(translationKeysToFetch).subscribe( textsObject => {
          Object.keys(this.translationTexts).forEach(key => {
            const translationKey = this.translationTexts[key];
            if (textsObject[translationKey]) {
              this.translationTexts[key] = textsObject[translationKey];
            }
          });
        });
      }

    ngOnInit() {
      this.resetCollabSkill();
      this.loaderService.showOverlayLoader();
      this.route.params.subscribe(async (params: Params) => {
        // resetCollabSkill fn should be called before setting itemId
        this.resetCollabSkill();
        if (this.router.url.indexOf("/teacher/")>-1) {
          this.isTeacher = true;
        }
        let classData:any =  this.activityViewHelperService.getProductClass();
        if(classData && classData['instructor'] && classData['instructor']['uuid']) {
          // Make isClassExpired = true if class has expired, so that Ready button doesn't show up in expired classes for student view(https://github.com/comprodls/nemo-micro-learning-path/issues/407#issuecomment-612005530)
          if(classData.enddate && new Date(classData.enddate) < new Date()) {
            this.isClassExpired = true;
          }
        }
        this.itemId = params.itemId;
        
        const navigationArray = this.activityViewHelperService.getNavigationMeta();
        if (navigationArray[navigationArray.length - 1]['item-code'] === this.itemId) {
          this.isLastActivity = true;
        }

        /*
          * @ref: NEMO-4032 AC's
          * If the student has one active group
            --------------------------------------------------
            In Analytics, the activity is completed/submitted.
              1) Use the groupid from analytics to show the group name and members.
              2) If the groupid is same as active group, show the normal view
              3) If groupid is different and that group is archived, show the archived view (see UI attachments NEMO-4032)
              4) In both cases (#2, #3), show Next activity button

            In Analytics, the activity is not completed/ not submitted  (Not started, In progress, resubmission requested)
              1) Use the active group of the learner to show the group name and members.
              2) Use the groupid of the active group to get the comments and group record

          * If the student does not have any active group and part of one or more archived groups
            -----------------------------------------------------------
            In Analytics, the activity is completed/submitted.
              1) Use the groupid from analytics to show the group name and members.
              2) If groupid is different and that group is archived, show the archived view (see UI attachments NEMO-4032)

              In Analytics, the activity is not completed/ not submitted  (Not started, In progress, resubmission requested)
              1) Similar to No groups UI, but warning message will be different

          * If the student is not part of any group
            -----------------------------------------------------------
            No Groups UI
        */
        let analyticsGroupId = this.activityViewHelperService.getAnalyticsGroupIdOfAnItem(this.itemId, false)
        if(analyticsGroupId) {
          this.groupId = analyticsGroupId;
        } else {
          let activeGroupId = this.route.snapshot.queryParamMap.get('group') || this.groupHelperService.getLearnerActiveGroupId();
          this.groupId = activeGroupId;
        }
        let allGroupsData:any = this.groupHelperService.getGroups();
        if(allGroupsData && allGroupsData.length) {
          allGroupsData.find((groupInfo)=> {
            this.isGroupArchived = true;
            if(groupInfo.uuid === this.groupId) {
              this.isGroupArchived = groupInfo.archived;
              this.groupDetails.title = groupInfo.title;
              return true;
            }
          })
        }
        if(this.groupId) {
          const extUserId = this.authService.getUserData().extUserId;
          const productCode = this.activityViewHelperService.getProductViewData().productDetails.productcode;
          this.lastReadCommentkey = `${appSetting.localStorage.lastReadCommentTimestamp}.${extUserId}.${productCode}.${this.groupId}.${this.itemId}`;
          this.lastReadCommentTime = localStorage.getItem(this.lastReadCommentkey) ? new Date(parseInt(localStorage.getItem(this.lastReadCommentkey))) : '';
          this.loadComments();
          // start 30 secs setInterval to poll new comments
          this.pollingSetInterval = setInterval( () => {
            this.loadNewComments();
          }, 1000 * 30);
        }
      });

      // Recording collab activity complete case
      this.completeCollabActivity = this.activityViewHelperService.completeCollabActivity.subscribe((res)=> {
        this.showSubmitLoader = false;
        this.closecollabConfirmationBtn.nativeElement.click();
      })

      // Recording collab activity completion with error
      this.changesNotSavedSubscription = this.activityViewHelperService.changesNotSaved.subscribe(()=> {
        this.showSubmitLoader = false;
        this.closecollabConfirmationBtn.nativeElement.click();
          setTimeout(() => {
            this.errorType = 'SUBMISSION_ERROR';
            this.showErrorModalBtn.nativeElement.click();
          }, 500);
      })
    }

    ngAfterViewInit() {
      if (window['ResizeObserver']) {
        // ResizeObserver doesn't work on all the browsers, so checking for support beforehand
        try {
          this.contentHeightObserver = new window['ResizeObserver'](this.checkScroll);
          this.contentHeightObserver.observe(this.contentContainer.nativeElement);
        } catch (error) { /* ResizeObserver was a draft SPEC, so the SPEC may change and cause errors in future. */}
      }
      this.ngZone.runOutsideAngular(() => {
        this.contentContainer.nativeElement.addEventListener('scroll', this.checkScroll, true);
      });
      if(!this.groupId) {
        setTimeout(this.checkScroll, 250);
      }
      this.loaderService.hideLoader();
      // Show progress bar
      this.activityViewHelperService.progressBarSubject.next({ visible: true });
    }

    resetCollabSkill(){
      if (this.itemId) {
        // If navigating from one collab to another collab, clear the text box.
        this.textEditor.setSavedInput('');
      }
      this.groupDetails = {};
      this.groupMembers = [];
      this.groupId = '';
      this.isGroupArchived = false;
      this.itemId = '';
      this.latestCommentId = '';
      this.hiddenInst = false; 
      this.scrollControlVisible = false;
      this.scrollUp = false;
      this.isTeacher = false;
      this.isClassExpired = false;
      this.commentsData = null;
      this.isLastActivity = false;
      this.lastReadCommentTime = '';
      this.lastReadCommentkey = '';
      this.serverClientTimeOffsetMs = null;

      if (this.timeElapsedCounter) {
        window.clearInterval(this.timeElapsedCounter);
      }
      if (this.pollingSetInterval) {
        window.clearInterval(this.pollingSetInterval);
      }
    }

    checkScroll = ():void => {
      const containerElement = this.contentContainer.nativeElement;
      const visibility = containerElement.scrollHeight > containerElement.clientHeight;
      const isScrollDirectionUp = containerElement.clientHeight + containerElement.scrollTop + 1 >= containerElement.scrollHeight;
      if (this.scrollControlVisible !== visibility || this.scrollUp !== isScrollDirectionUp) {
        this.ngZone.run(() => {
          this.scrollControlVisible = visibility;
          this.scrollUp = isScrollDirectionUp;
        });
      }
    }

    scrollThePage(commentId?) {
      const containerElement = this.contentContainer.nativeElement;
      let scrollTop;
      if (commentId) {
        const commentCard = document.getElementById('comment-' + commentId);
        scrollTop = commentCard ? commentCard.offsetTop : (containerElement.scrollHeight - containerElement.clientHeight);
      } else if (this.scrollUp) {
        scrollTop = 0;
      } else {
        scrollTop = containerElement.scrollHeight - containerElement.clientHeight;
      }
      $(containerElement).animate( { scrollTop }, 800);
    }

    async loadComments() {
      this.showCommentsLoader = true;
      const response = await this.commentsService.getComments(this.groupId, this.itemId, this.lastReadCommentkey, { fetchGroupMembers: true, lastReadCommentTime: this.lastReadCommentTime});

      if (!response.success) {
        this.errorType = response.error.errorCode;
        this.showErrorModalBtn.nativeElement.click();
        this.showCommentsLoader = false;
        return;
      }

      this.updateServerClientTimeOffsetFromResponse(response);

      response.commentsData.results = response.commentsData.results.reverse();
      response.commentsData.results.forEach( comment => {
        comment['elapsedTime'] = this.setRelativeTimeStamp(comment);
      });

      if (response.commentsData.results.length > 0) {
        this.latestCommentId = response.commentsData.results[response.commentsData.results.length - 1].id;
      }

      if (response.groupMembers && response.groupMembers.length > 0) {
        response.groupMembers.forEach( (member) => {
          let name = member.first_name + ' ' + member.last_name;
          this.groupMembers.push(name);
        });
        this.groupDetails = response.groupMembers[0].group;
      }

      this.commentsData = response.commentsData;
      setTimeout(this.checkScroll, 250);
      this.showCommentsLoader = false;

      // start 1 min counter to update timestamps
      this.timeElapsedCounter = setInterval( () => {
        this.commentsData.results.forEach( comment => {
          comment['elapsedTime'] = this.setRelativeTimeStamp(comment);
        });
      }, 1000 * 60);
    }

    async loadOldComments() {
      this.showCommentsLoader = true;
      const refComment = this.commentsData.results[0].id;

      const oldCommentsRes = await this.commentsService.getComments(this.groupId, this.itemId, this.lastReadCommentkey, {
        limit: 20,
        refComment,
        lastReadCommentTime: this.lastReadCommentTime
      });

      if (!oldCommentsRes.success) {
        this.errorType = oldCommentsRes.error.errorCode;
        this.showErrorModalBtn.nativeElement.click();
        this.showCommentsLoader = false;
        return;
      }

      this.updateServerClientTimeOffsetFromResponse(oldCommentsRes);

      this.commentsData.next = oldCommentsRes.commentsData.next;

      oldCommentsRes.commentsData.results.forEach(comment => {
        comment['elapsedTime'] = this.setRelativeTimeStamp(comment);
        this.commentsData.results.unshift(comment);
      });
      this.showCommentsLoader = false;
    }

    async loadNewComments () {
      const newCommentsRes = await this.commentsService.getComments(this.groupId, this.itemId, this.lastReadCommentkey, {
        refComment: this.latestCommentId,
        polling: true,
        lastReadCommentTime: this.lastReadCommentTime
      });

      if (!newCommentsRes.success || (newCommentsRes.commentsData.results.length < 1) ) {
        // No action needed if polling request fails or no comments are fetched
        return;
      }

      this.updateServerClientTimeOffsetFromResponse(newCommentsRes);
      
      this.latestCommentId = newCommentsRes.commentsData.results[0].id;

      const tempCommentsArray = [];
      // Handling the case where the First Time loading of comments failed
      if (this.commentsData && this.commentsData.results) {
        tempCommentsArray.push(...this.commentsData.results);
      } else {
        this.commentsData = {
          next: newCommentsRes.commentsData.next,
        };
      }

      newCommentsRes.commentsData.results.forEach(comment => {
        // Filter out comments which are still present
        if (tempCommentsArray.find(c => c.id === comment.id) === undefined) {
          comment['elapsedTime'] = this.setRelativeTimeStamp(comment);
          tempCommentsArray.push(comment);
        }
      });

      tempCommentsArray.sort( (commA, commB) => {
        const dateA = new Date(commA.time);
        const dateB = new Date(commB.time);
        return dateA.getTime() - dateB.getTime();
      });

      this.commentsData.results = tempCommentsArray;
      setTimeout(this.checkScroll, 250);
    }

    postComment(userText) {
      const newCommentRes = this.commentsService.addComment(this.groupId, this.itemId, userText);
      const newComment = newCommentRes.commentObject;

      // Subscribe to subject to catch errors
      const newCommentSubscription = newCommentRes.subject.subscribe( (newCommentResponse: any) => {
        if (!newCommentResponse.success) {
          this.errorType = newCommentResponse.error.errorCode;
          this.showErrorModalBtn.nativeElement.click();
          const cIndex = this.commentsData.results.findIndex( c => newCommentResponse.commentObject.id === c.id);
          if (cIndex > -1) {
            this.commentsData.results.splice(cIndex, 1);
            this.textEditor.setSavedInput(userText);
          }
        } else {
          this.updateServerClientTimeOffsetFromResponse(newCommentResponse);
          newComment['elapsedTime'] = this.setRelativeTimeStamp(newComment);
        }
        newCommentSubscription.unsubscribe();
      });

      this.commentsData.results.push(newComment);

      setTimeout( () => {
        this.scrollThePage(newComment.id);
      }, 250);
    }

    setRelativeTimeStamp(commentObject: any) {
      const commentDate = new Date(commentObject.time + 'Z');
      const totalTimeInSeconds = Math.floor(((Date.now() + this.serverClientTimeOffsetMs) - commentDate.getTime()) / 1000);

      if (totalTimeInSeconds < 60) {
        return this.translationTexts.JUST_NOW;
      } else if ((totalTimeInSeconds / 60) < 60) {
        return Math.floor(totalTimeInSeconds / 60)
            + (Math.floor(totalTimeInSeconds / 60) === 1 ? this.translationTexts.MINUTE_AGO : this.translationTexts.MINUTES_AGO);
      } else if ((totalTimeInSeconds / 3600) < 24) {
        return Math.floor(totalTimeInSeconds / 3600)
            + (Math.floor(totalTimeInSeconds / 3600) === 1 ? this.translationTexts.HOUR_AGO : this.translationTexts.HOURS_AGO);
      } else if ((totalTimeInSeconds / (3600 * 24)) < 7) {
        return Math.floor(totalTimeInSeconds / (3600 * 24))
           + (Math.floor(totalTimeInSeconds / (3600 * 24)) === 1 ? this.translationTexts.DAY_AGO : this.translationTexts.DAYS_AGO);
      } else {
        const dateArray = commentDate.toDateString().split(' ');
        return (dateArray[0] + ', ' + dateArray[2] + ' ' + dateArray[1] + ', ' + dateArray[3])
      }
    }

    /**
     * function to update the difference between server time and client time. called periodically after get/add comments to adjust the offset accordingly 
     * @param response 
     */
    private updateServerClientTimeOffsetFromResponse(response: any) {
      const serverTime = response?.serverTime;
      this.serverClientTimeOffsetMs = serverTime? (serverTime - Date.now()) : Date.now();
    }

    submitCollabActivity() {
      this.showSubmitLoader = true;
      let options = {
        group: {
          id: this.groupId
        }
      };
      this.activityViewHelperService.getXAPIRunnerObject().generateCustomStatement(this.collabTask.itemid, "completed", options);
    }

    ngOnDestroy() {
      if(this.completeCollabActivity) {
        this.completeCollabActivity.unsubscribe();
      }
      if(this.changesNotSavedSubscription) {
        this.changesNotSavedSubscription.unsubscribe();
      }
      if(this.routeSubscription) {
        this.routeSubscription.unsubscribe();
      }

      this.resetCollabSkill();

      if (this.contentHeightObserver && this.contentHeightObserver.disconnect) {
        this.contentHeightObserver.disconnect();
      }
      // Re-rendering of the progress bar
      this.activityViewHelperService.progressBarSubject.next({ visible: false });
    }

}
